"use client";

import Cookie from "js-cookie";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { BiSearch } from "react-icons/bi";

import { Product } from "@/@types";
import { EMPTY_FACETS, FACET_KEYS, Facets } from "@/@types/filters";
import ProductList from "@/components/ProductList";
import Search from "@/components/Search";
import {
  PAGE_SIZE,
  appendUnique,
  countActiveFilters,
  parseFiltersFromParams,
  queryKey,
  replacePageInUrl,
  searchProducts,
} from "@/lib/products";

import styles from "./Busca.module.scss";

const listFormat = new Intl.ListFormat("pt-BR", { type: "conjunction" });

const quoted = (terms: string[]) => listFormat.format(terms.map((term) => `“${term}”`));

const SKELETON_CARDS = 8;

export default function Busca() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();

  const searchParamsKey = searchParams.toString();
  const termFromUrl = searchParams.get("s") ?? "";

  const [value, setValue] = useState(termFromUrl);
  // Já nasce carregando quando a URL traz um termo: o esqueleto sai no primeiro
  // render, sem um instante de tela vazia antes do efeito disparar a busca.
  const [loading, setLoading] = useState(Boolean(termFromUrl.trim()));
  // O termo que gerou os resultados na tela, não o que acabou de entrar na URL:
  // assim o resumo não troca de texto antes da resposta chegar.
  const [searchedTerm, setSearchedTerm] = useState("");
  // A consulta que gerou os resultados na tela (ver queryKey).
  const [resultsKey, setResultsKey] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [semanticProducts, setSemanticProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [facets, setFacets] = useState<Facets>(EMPTY_FACETS);
  const [matchedTerms, setMatchedTerms] = useState<string[]>([]);
  const [droppedTerms, setDroppedTerms] = useState<string[]>([]);

  // Só a busca mais recente pode mexer na tela: uma resposta atrasada de uma
  // busca anterior não pode sobrescrever resultados nem encerrar o loading.
  const latestRequest = useRef(0);

  const runSearch = useCallback(async (term: string, params: URLSearchParams) => {
    const request = ++latestRequest.current;

    if (!term.trim()) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const page = Number.parseInt(params.get("pagina") ?? "1", 10) || 1;

      const response = await searchProducts(
        {
          search: term,
          page,
          filters: parseFiltersFromParams(params),
        },
        { secret: Cookie.get("secret"), auth: Cookie.get("auth") }
      );

      if (request !== latestRequest.current) return;

      setSearchedTerm(term.trim());
      setResultsKey(queryKey(params));
      setProducts(response.products ?? []);
      setSemanticProducts(response.semantic_products ?? []);
      setTotal(response.total ?? 0);
      setTotalCapped(Boolean(response.total_capped));
      setFacets(response.facets);
      setMatchedTerms(response.matched_terms ?? []);
      setDroppedTerms(response.dropped_terms ?? []);
    } catch {
      if (request !== latestRequest.current) return;

      toast.error("Não foi possível buscar agora. Tente de novo.");
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, []);

  // "Ver mais": pede só a próxima página da busca que está na tela e acrescenta.
  // A página vai para a URL sem navegar (ver replacePageInUrl).
  const loadMore = useCallback(async () => {
    const request = latestRequest.current;
    const shown = new URLSearchParams(resultsKey);

    const response = await searchProducts(
      {
        search: searchedTerm,
        filters: parseFiltersFromParams(shown),
        offset: products.length,
        limit: PAGE_SIZE,
      },
      { secret: Cookie.get("secret"), auth: Cookie.get("auth") }
    );

    // Uma busca nova começou enquanto esta página vinha: descarta.
    if (request !== latestRequest.current) return;

    const items = appendUnique(products, response.products ?? []);

    setProducts(items);
    // Sem nada novo, a lista acabou: evita um "Ver mais" que nunca termina.
    setTotal(items.length > products.length ? response.total ?? total : items.length);
    setTotalCapped(Boolean(response.total_capped));
    replacePageInUrl(Math.ceil(items.length / PAGE_SIZE));
  }, [resultsKey, searchedTerm, products, total]);

  // Refaz a busca sempre que o termo, os filtros ou a página mudam na URL —
  // é o que faz o mesmo drawer de filtros funcionar aqui.
  useEffect(() => {
    const params = new URLSearchParams(searchParamsKey);
    const term = params.get("s") ?? "";

    setValue(term);
    // Sem termo, volta ao estado inicial em vez de manter a busca anterior.
    if (!term.trim()) setSearchedTerm("");
    runSearch(term, params);
  }, [searchParamsKey, runSearch]);

  function onSubmit() {
    const params = new URLSearchParams(searchParamsKey);
    const term = value.trim();

    // Termo novo zera as facetas: elas saem dos resultados da busca anterior
    // (cor Verde de "prato" viraria "taça verde" sem ninguém pedir). Carne ou
    // leite fica, porque é preferência de quem busca, não do resultado.
    if (term !== (params.get("s") ?? "").trim()) {
      FACET_KEYS.filter((key) => key !== "kosher_tipo").forEach((key) => params.delete(key));
      params.delete("filters");
    }

    // Links antigos traziam o "Apenas kosher", que saiu da busca.
    params.delete("kosher");

    if (term) {
      params.set("s", term);
    } else {
      params.delete("s");
    }

    // Termo novo recomeça a paginação.
    params.delete("pagina");

    replace(`${pathname}?${params.toString()}`);
  }

  // Zera tudo, não só o texto: os filtros dependiam daquela busca.
  function clearSearch() {
    setValue("");
    replace(pathname);
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.value);
  };

  const hasResults = products.length > 0 || semanticProducts.length > 0;
  // Primeira busca: ainda não há o que mostrar, então entra o esqueleto.
  const firstLoad = loading && !searchedTerm;
  // Busca nova com resultados antigos na tela: esmaece até a resposta chegar.
  // Pedir a próxima página ("Ver mais") não conta como busca nova.
  const refreshing =
    loading && Boolean(searchedTerm) && queryKey(new URLSearchParams(searchParamsKey)) !== resultsKey;

  // O estado vazio descreve a consulta que gerou a tela, não a que está carregando.
  const shownQuery = new URLSearchParams(resultsKey);
  const shownFilters = parseFiltersFromParams(shownQuery);
  const shownFacets = countActiveFilters(shownFilters) > 0;
  // Os kosher ficam fora da busca até marcar carne ou leite no Filtrar. A barra
  // aparece mesmo sem resultado direto quando o termo casa com algum kosher (é o
  // único caminho até eles) ou quando carne/leite já está marcado (para desmarcar).
  const kosherInFilter =
    shownFilters.kosher_tipo.length > 0 || facets.kosher_tipo.some((option) => option.count > 0);

  return (
    <>
      <Search
        onChange={onChange}
        onSubmit={onSubmit}
        onClear={clearSearch}
        loading={loading}
        value={value}
        placeholder="Nome ou código do produto"
        autoFocus={!termFromUrl}
        action="/busca"
        name="s"
      />

      <p className={styles.srOnly} role="status">
        {loading ? "Buscando produtos…" : ""}
      </p>

      {!termFromUrl && (
        <div className={styles.empty}>
          <BiSearch aria-hidden="true" />
          <p>O que você está procurando?</p>
          <span>Ex.: prato fundo verde, PRF245</span>
        </div>
      )}

      {termFromUrl && firstLoad && (
        // Imita resumo, barra e grade para a página não pular quando a resposta chega.
        <div className={styles.skeleton} aria-hidden="true">
          <div className={styles.skeleton__title} />
          <div className={styles.skeleton__toolbar} />
          <div className={styles.skeleton__grid}>
            {Array.from({ length: SKELETON_CARDS }, (_, index) => (
              <div key={index} />
            ))}
          </div>
        </div>
      )}

      {searchedTerm && (
        // Esmaece resumo, grade e aviso, mas não o wrapper: a gaveta de filtros
        // (position: fixed, sem portal) vive aqui dentro e ficaria translúcida.
        <div aria-busy={loading}>
          {hasResults && (
            <header className={styles.summary} data-stale={refreshing || undefined}>
              <h2>Resultados para “{searchedTerm}”</h2>

              {products.length === 0 ? (
                <p>Nada encontrado com esses termos. Veja itens semelhantes.</p>
              ) : (
                droppedTerms.length > 0 && (
                  <p>
                    Não encontramos nada com {quoted(droppedTerms)}. Mostrando os que combinam com{" "}
                    <strong>“{matchedTerms.join(" ")}”</strong>.
                  </p>
                )
              )}
            </header>
          )}

          {/* Sem resultado e sem faceta: sugere o próximo passo em vez de só
              avisar. Com faceta ativa, quem responde é a lista ("Limpar filtros"). */}
          {!hasResults && !shownFacets && !kosherInFilter ? (
            <div className={styles.empty} data-stale={refreshing || undefined}>
              <BiSearch aria-hidden="true" />
              <p>Nenhum produto encontrado para “{searchedTerm}”</p>
              <span>Confira a grafia ou busque pelo código do produto (ex.: PRF245).</span>
            </div>
          ) : (
            <ProductList
              products={products}
              semanticProducts={semanticProducts}
              total={total}
              totalCapped={totalCapped}
              stale={refreshing}
              onLoadMore={loadMore}
              facets={facets}
              // A barra (facetas e contagem) descreve só os resultados diretos, mas
              // fica quando o kosher está em jogo (ver kosherInFilter).
              showFilter={products.length > 0 || kosherInFilter}
              label={`Nenhum produto encontrado para “${searchedTerm}”`}
            />
          )}
        </div>
      )}
    </>
  );
}
